import { observability } from "../observability";
import { EVENT_ROUTING } from "./constants";

// Supported analytics provider names
type ProviderName = "segment" | "observability" | "microsoft_ads";

// Provider implementations - encapsulates provider-specific logic
const providers: Record<
  ProviderName,
  {
    track: (event: string, properties: any) => void;
    identify: (userId: string | undefined, traits: any, email?: string) => void;
    page: (name?: string, properties?: any) => void;
  }
> = {
  segment: {
    track: (event: string, properties: any) => {
      window.analytics?.track(event, properties);
    },
    identify: (userId: string | undefined, traits: any) => {
      if (userId) {
        window.analytics?.identify(userId, traits);
      } else {
        window.analytics?.identify(traits);
      }
    },
    page: (name?: string, properties?: any) => {
      window.analytics?.page(name, properties);
    },
  },

  // Observability backends (Datadog RUM, Grafana Faro), behind src/observability
  observability: {
    track: (event: string, properties: any) => {
      observability.event(event, properties);
    },
    identify: (userId: string | undefined, traits: any) => {
      observability.identify(userId, traits);
    },
    page: () => {},
  },

  microsoft_ads: {
    track: (event: string, properties: any) => {
      window.uetq = window.uetq || [];
      window.uetq.push("event", event, properties);
    },
    identify: (_userId: string | undefined, _traits: any, email?: string) => {
      window.uetq = window.uetq || [];
      window.uetq.push("set", email ? { pid: { em: email } } : { pid: {} });
    },
    page: () => {
      // Microsoft Ads doesn't track page events
    },
  },
};

// Dispatches track events to the appropriate providers based on EVENT_ROUTING
export function dispatchTrack(event: string, properties: any): void {
  const routingKey = EVENT_ROUTING[event] ? event : "track:*";
  const providerNames = EVENT_ROUTING[routingKey] || [];

  providerNames.forEach((name) => {
    try {
      providers[name as ProviderName]?.track(event, properties);
    } catch (error) {
      console.error(`Failed to dispatch track to ${name}`, error);
    }
  });
}

// Dispatches identify events to the appropriate providers based on EVENT_ROUTING
export function dispatchIdentify(userId: string | undefined, traits: any, email?: string): void {
  const providerNames = EVENT_ROUTING["identify"] || [];

  providerNames.forEach((name) => {
    try {
      providers[name as ProviderName]?.identify(userId, traits, email);
    } catch (error) {
      console.error(`Failed to dispatch identify to ${name}`, error);
    }
  });
}

// Dispatches page events to the appropriate providers based on EVENT_ROUTING
export function dispatchPage(name?: string, properties?: any): void {
  const providerNames = EVENT_ROUTING["page"] || [];

  providerNames.forEach((providerName) => {
    try {
      providers[providerName as ProviderName]?.page(name, properties);
    } catch (error) {
      console.error(`Failed to dispatch page to ${providerName}`, error);
    }
  });
}
